/**
 * This is the starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import React, { useState } from 'react';
import { ApolloProvider, ApolloClient, InMemoryCache } from '@apollo/client';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider } from 'styled-components';
import { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import {
  ContentServiceV1 as ContentService,
  ContentChangeListener,
} from '@volkswagen-onehub/audi-content-service';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { createTrackingService } from '@volkswagen-onehub/oneaudi-os-adapter-tracking';
import { getGraphqlGatewayUrl } from './hooks/getGraphqlGatewayUrl';
import VehicleProvider from './components/VehicleProvider';
import i18n from '../i18n';
import GlobalStyles from './styles';
import { COLORS } from './styles/variables';
import VehicleHeader from './components/VehicleHeader';
import ErrorBoundary from './components/ErrorBoundary';
import { AppContent, mapContent } from './content-mapping';
import { IS_TIER3_PAGE } from './constants';
// import SEO from './components/SEO/SEO';

interface FeatureAppProps {
  configProvider?: GfaServiceConfigProviderV1;
  contentService?: ContentService;
  trackingService?: TrackingServiceV2;
}

const apolloClient = new ApolloClient({
  uri: getGraphqlGatewayUrl(),
  cache: new InMemoryCache(),
  name: __FEATURE_APP_NAME__,
  version: __FEATURE_APP_VERSION__,
});

const FeatureApp: React.FC<FeatureAppProps> = ({ ...inherited }: FeatureAppProps) => {
  const { trackingService: trackingServiceInherited, contentService } = inherited;

  let trackingService = {} as TrackingServiceV2;

  if (typeof window !== 'undefined') {
    trackingService = trackingServiceInherited || createTrackingService(__FEATURE_APP_NAME__);
  }

  const [rawContent, setRawContent] = useState(contentService?.getContent() as AppContent);
  const content = mapContent(rawContent);

  contentService?.onContentChange(setRawContent as ContentChangeListener);

  React.useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    trackingService.featureAppName = __FEATURE_APP_NAME__;
    const readyEvent = {
      event: {
        attributes: {
          componentName: __FEATURE_APP_NAME__,
          elementName: '',
          label: '',
          clickID: '',
          pos: '',
          targetURL: '',
          value: '',
        },
        eventInfo: {
          eventAction: 'feature_app_ready',
          eventName: `${__FEATURE_APP_NAME__} - Feature App ready`,
        },
      },
    };
    trackingService.track(readyEvent);
    // eslint-disable-next-line
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      {/* @ts-ignore:next-line */}
      <ErrorBoundary>
        <ThemeProvider theme={COLORS}>
          <ApolloProvider client={apolloClient}>
            <VehicleProvider isTier3={IS_TIER3_PAGE}>
              <div className="resetWrapper">
                {/* <SEO /> */}
                <VehicleHeader cta360View={content} />
              </div>
            </VehicleProvider>
          </ApolloProvider>
          <GlobalStyles />
        </ThemeProvider>
      </ErrorBoundary>
    </I18nextProvider>
  );
};

export default FeatureApp;
